import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetcher } from '../../../lib/api-fetcher';
import { useDebounce } from '../../../lib/hooks';
import { Input } from '../../../components/ui/FormField';
import { Button } from '../../../components/ui/Button';

export interface SubscriberSearchResult {
  id: string;
  customerName: string;
  ticketNumbers: number[];
  schemeId: string;
  schemeName: string;
  schemeNumber: string;
  status: string;
}

function useSubscriberSearch(query: string) {
  const debouncedQuery = useDebounce(query, 300);
  return useQuery({
    queryKey: ['subscribers', 'search', debouncedQuery],
    queryFn: async () => {
      const params = new URLSearchParams({ q: debouncedQuery });
      const res = await fetcher.get<SubscriberSearchResult[] | { data: SubscriberSearchResult[] }>(`/staff/v1/subscribers/search?${params.toString()}`);
      return Array.isArray(res) ? res : res?.data ?? [];
    },
    enabled: debouncedQuery.trim().length >= 2,
  });
}

interface SubscriberSearchInputProps {
  value: string;
  onChange: (subscriberId: string, subscriber?: SubscriberSearchResult) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function SubscriberSearchInput({ value, onChange, disabled, placeholder = 'Search subscriber name or ticket...' }: SubscriberSearchInputProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedLabel, setSelectedLabel] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { data: results = [], isFetching } = useSubscriberSearch(searchQuery);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (subscriber: SubscriberSearchResult) => {
    const label = `${subscriber.customerName} — ${subscriber.schemeNumber} (Ticket ${subscriber.ticketNumbers.join(', ')})`;
    setSelectedLabel(label);
    setSearchQuery('');
    setIsOpen(false);
    onChange(subscriber.id, subscriber);
  };

  const displayValue = selectedLabel && !isOpen ? selectedLabel : searchQuery;

  return (
    <div className="relative" ref={containerRef} style={{ width: '100%' }}>
      <Input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setSelectedLabel('');
          setIsOpen(true);
          if (!e.target.value) onChange('');
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
        autoComplete="off"
      />
      {isOpen && !disabled && searchQuery.trim().length >= 2 && (
        <div className="absolute z-10 w-full mt-1 bg-surface-0 border border-surface-3 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {isFetching ? (
            <div className="px-3 py-2 text-sm text-text-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-text-muted">No subscribers found</div>
          ) : (
            results.map((subscriber) => (
              <Button
                key={subscriber.id}
                type="button"
                variant="ghost"
                onClick={() => handleSelect(subscriber)}
                className={`w-full h-auto justify-start rounded-none text-left px-3 py-2 text-sm font-normal ${value === subscriber.id ? 'bg-accent-bg' : ''}`}
              >
                <span className="font-medium text-text-primary">{subscriber.customerName}</span>
                <span className="ml-2 text-xs text-text-muted">{subscriber.schemeNumber} · Ticket {subscriber.ticketNumbers.join(', ')}</span>
              </Button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
